import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';

function Sidebar({ mobileOpen, desktopOpen, handleDrawerToggle }) {
  const drawerWidth = 240;
  const collapsedWidth = 65;
  const location = useLocation();

  const menuItems = [
    { text: 'Dashboard', icon: <GridViewRoundedIcon />, page: 'dashboard' },
    {
      text: 'Pending Approvals',
      icon: <PendingActionsRoundedIcon />,
      page: 'pending-approvals',
    },
    { text: 'Teachers', icon: <SchoolRoundedIcon />, page: 'teachers' },
  ];

  // Check if current route matches the menu item
  const isActive = (page) => {
    return (
      location.pathname === `/admin/${page}` ||
      (page === 'dashboard' && location.pathname === '/admin')
    );
  };

  const drawer = (isCollapsed) => (
    <div>
      <div className="text-center py-6">
        <a href="/" className="mb-4">
          <img className="w-[80px] mx-auto" src="/assets/images/logo.png" alt="Logo" />
        </a>
        <h1 className="text-2xl font-bold text-white">{isCollapsed ? 'AP' : 'Admin Panel'}</h1>
      </div>
      <Divider className="!border-white/20" />
      <List className="!mt-4">
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={`/admin/${item.page}`}
              selected={isActive(item.page)}
              onClick={() => {
                if (mobileOpen) handleDrawerToggle();
              }}
              sx={{
                justifyContent: isCollapsed ? 'center' : 'initial',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                },
              }}
            >
              <ListItemIcon
                className={isActive(item.page) ? '!text-secondary' : '!text-white/30'}
                sx={{
                  minWidth: isCollapsed ? 0 : 56,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary={item.text}
                  className={
                    isActive(item.page)
                      ? '!text-secondary [&_.MuiTypography-root]:font-primary'
                      : '[&_.MuiTypography-root]:font-primary'
                  }
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <div>
      <Box
        component="nav"
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedWidth },
          flexShrink: { sm: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          className="[&_.MuiDrawer-paper]:bg-gradient-secondary [&_.MuiDrawer-paper]:text-white [&_.MuiDrawer-paper]:drop-shadow-md"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer(false)}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          className="[&_.MuiDrawer-paper]:bg-gradient-secondary [&_.MuiDrawer-paper]:text-white [&_.MuiDrawer-paper]:drop-shadow-md"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : collapsedWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer(!desktopOpen)}
        </Drawer>
      </Box>
    </div>
  );
}

export default Sidebar;
